import type { ReactNode } from 'react';
import { cx } from '../cx';

export type ScoreBarProps = {
  label: ReactNode;
  /** 0 to 1. */
  value: number;
  /** The dimension's fixed weight, formatted ("25%"). */
  weight: ReactNode;
  /** Words for screen readers ("Pay: strong"), since the bar has no number on purpose. */
  valueText: string;
  className?: string;
};

/** Clamp to 0..1 and turn into a CSS width. Pure, exported for tests. */
export const barWidth = (value: number): string =>
  `${Math.round(Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0)) * 1000) / 10}%`;

/**
 * One bar per Employer Score dimension with its weight: bars, never numbers. Ink (warm white in
 * dark), never the employer's colour: the score is our judgement and wears our colours.
 */
export function ScoreBar({ label, value, weight, valueText, className }: ScoreBarProps) {
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold text-jc-ink">{label}</span>
        {weight !== '' ? <span className="text-xs tabular-nums text-jc-ink-secondary">{weight}</span> : null}
      </div>
      <div role="img" aria-label={valueText} className="h-2.5 overflow-hidden rounded-jc-info bg-jc-elevated">
        <div className="h-full rounded-jc-info bg-jc-ink" style={{ width: barWidth(value) }} />
      </div>
    </div>
  );
}
